import {Record} from 'immutable';
import tinycolor from 'tinycolor2';
import {isNone} from '~/data/accessors/color';

function isNumber(val) {
  return typeof val === 'number';
}

function normalizeRgb({r, g, b, red, green, blue}) {
  const rgb = {
    red: isNumber(red) ? red : isNumber(r) ? r : null,
    green: isNumber(green) ? green : isNumber(g) ? g : null,
    blue: isNumber(blue) ? blue : isNumber(b) ? b : null
  };
  return {
    ...rgb,
    r: rgb.red,
    g: rgb.green,
    b: rgb.blue,
    R: rgb.red,
    G: rgb.green,
    B: rgb.blue
  };
}

export function hexToRgb(hex) {
  return normalizeRgb(tinycolor(hex).toRgb());
}

function getCssString({red, green, blue}) {
  return isNone({red, green, blue})
    ? 'transparent'
    : `rgb(${red}, ${green}, ${blue})`;
}

function getHexString(rgb) {
  const hex = tinycolor(rgb).toHex();
  return isNone(rgb) ? `${hex}00` : hex;
}

export default class Color extends Record({
  name: 'None',
  id: '',
  alternative_id: '',
  red: null,
  green: null,
  blue: null,
  secondary: null,
  status: '',
  simple_names: [],
  subtle_inks: [],
  cssString: 'transparent',
  hexString: '00000000',
  r: null,
  g: null,
  b: null,
  R: null,
  G: null,
  B: null
}) {
  constructor(color) {
    // If color is an existing Color record, turn it back into a POJO before proceeding
    const {hex, secondary, ...args} = color.toJS ? color.toJS() : color;
    // If the color object has a hex string property, use that instead of looking for RGB values
    const rgb = hex ? hexToRgb(hex) : normalizeRgb(args);
    super(
      {
        ...args,
        ...rgb,
        secondary:
          secondary && typeof secondary === 'object'
            ? new SecondaryColor(secondary)
            : null,
        cssString: getCssString(rgb),
        hexString: getHexString(rgb)
      },
      'Color'
    );
  }
}

class SecondaryColor extends Record({
  red: null,
  green: null,
  blue: null,
  cssString: '',
  hexString: '',
  r: null,
  g: null,
  b: null,
  R: null,
  G: null,
  B: null
}) {
  constructor(secondary) {
    // If secondary is an existing Color record, turn it back into a POJO before proceeding
    const {hex, ...args} = secondary.toJS ? secondary.toJS() : secondary;
    // If the secondary object has a hex string property, use that instead of looking for RGB values
    const rgb = hex ? hexToRgb(hex) : normalizeRgb(args);
    super(
      {
        ...args,
        ...rgb,
        cssString: getCssString(rgb),
        hexString: getHexString(rgb)
      },
      'SecondaryColor'
    );
  }
}

const mmsColorRecords = {};
export function recordFromMmsColor(color) {
  return (
    mmsColorRecords[color.id] ||
    (function () {
      const colorHash = color.rgbs[0];
      let secondary = color.trim_rgb;
      if (
        !Number.isInteger(secondary?.red) &&
        !Number.isInteger(secondary?.green) &&
        !Number.isInteger(secondary?.blue)
      ) {
        secondary = null;
      }
      const colorRecord = new Color({
        ...colorHash,
        name: color.name,
        id: color.id,
        status: color.status,
        alternative_id: color.alternative_id,
        secondary,
        simple_names: color.simple_names,
        subtle_inks: color.subtle_inks
      });
      mmsColorRecords[color.id] = colorRecord;
      return colorRecord;
    })()
  );
}
